import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Tabs, 
  Tab, 
  Chip,
  Fade
} from '@mui/material';
import { styled } from '@mui/system';

const StyledTab = styled(Tab)({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  marginRight: '8px',
  marginBottom: '10px',
  borderRadius: '50px',
  transition: '0.3s',
  color: 'rgba(255, 255, 255, 0.6)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  minHeight: '40px',
  '&.Mui-selected': {
    color: '#004d40',
    backgroundColor: '#ffffff',
    borderColor: '#ffffff',
    fontWeight: 800,
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    borderColor: '#ffffff',
  }
});

const projectData = [
  { id: 1, category: 'Groundwater', title: 'ERT Survey & Assessment', desc: 'Geophysical investigations for source vulnerability.', img: '/images/image.png'},
  { id: 2, category: 'Groundwater', title: 'Pumping Tests', desc: 'Borewell capacity and water level logging.', img: '/images/ert_img1.png' },
  { id: 3, category: 'Mapping & RWH', title: 'Watershed Mapping', desc: 'Catchment area analysis and flow direction.', img: '/images/map_1.png' },
  { id: 4, category: 'Mapping & RWH', title: 'RWH Blueprint Design', desc: 'Detailed engineering drawings for recharge structures.', img: '/images/map_2.png' },
  { id: 5, category: 'STP & Wastewater', title: 'Prefab Packaged STP', desc: 'Compact sewage treatment units for industrial use.', img: '/images/stp_1.png' },
  { id: 6, category: 'STP & Wastewater', title: 'SBR Technology', desc: 'Sequential Batch Reactor implementation.', img: '/images/stp_2.png' },
  { id: 7, category: 'Agriculture', title: 'Smart Crop Monitoring', desc: 'Field photos after organic enhancer application.', img: '/images/agr_1.png' },
  { id: 8, category: 'Agriculture', title: 'Harvest Yield', desc: 'Successful harvesting of fruits and vegetables.', img: '/images/agr_2.png' },
];

const categories = ['All', 'Groundwater', 'STP & Wastewater', 'Mapping & RWH', 'Agriculture'];

const ServicesShowcase = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const filteredProjects =
    selectedTab === 0
      ? projectData
      : projectData.filter((project) => project.category === categories[selectedTab]);

  return (
    <Box
      id="gallery" 
      sx={{
        py: 10,
        scrollMarginTop: "80px",
        background: 'linear-gradient(135deg, #004d40 0%, #0d47a1 100%)',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ position: 'absolute', top: -50, right: -50, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>

        <Box textAlign="center" mb={6}>
          <Typography variant="overline" sx={{
            fontWeight: '800',
            letterSpacing: 2,
            color: '#a5d6a7', 
            bgcolor: 'rgba(255,255,255,0.1)',
            px: 2,
            py: 0.5,
            borderRadius: 10
          }}>
            GALLERY
          </Typography>

          <Typography variant="h3" fontWeight="700" sx={{ mt: 2, color: 'white' }}>
            Site Execution
          </Typography>

          <Typography variant="body1" sx={{
            mt: 2,
            maxWidth: '600px',
            mx: 'auto',
            color: 'rgba(255,255,255,0.8)'
          }}>
            Visualizing our commitment to sustainable water and environmental solutions.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ 
                '& .MuiTabs-flexContainer': { gap: 1, justifyContent: 'center' },
                '& .MuiTabScrollButton-root': { color: 'white' }
            }}
          >
            {categories.map((cat, index) => (
              <StyledTab key={index} label={cat} disableRipple />
            ))}
          </Tabs>
        </Box>

        <Grid container spacing={3}>
          {filteredProjects.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.id}>
              <Fade in={true} timeout={500}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: 'transparent', 
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        '& .img-container': { transform: 'scale(1.03)', boxShadow: '0 20px 40px rgba(0,0,0,0.4)' }
                    }
                  }}
                >
                  <Box 
                    className="img-container"
                    sx={{ 
                        borderRadius: 3, 
                        overflow: 'hidden', 
                        mb: 2, 
                        height: 200, 
                        boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                        transition: '0.3s ease'
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.img}
                      alt={item.title}
                      sx={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  </Box>

                  <Box sx={{ px: 1 }}>
                    <Chip
                      label={item.category}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '0.7rem',
                        height: 20,
                        mb: 1
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1.2 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
};

export default ServicesShowcase;